import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';

import { Severity } from './services/flash-messages/flash-message.model';
import { FlashMessagesService } from './services/flash-messages/flash-messages.service';
import { SpinnerService } from './services/spinner/spinner.service';
import { WindowService } from './services/window/window.service';
import { ClientAuthError } from 'msal';

@Injectable()
export class CustomErrorHandler extends ErrorHandler {
    constructor(private injector: Injector) {
        // The true parameter tells Angular to rethrow exceptions,
        // so operations like 'bootstrap' will result in an error when an error happens.
        // If we do not rethrow, bootstrap will always succeed.
        super();
    }

    handleError(error: any): void {
        super.handleError(error);

        // NOTE: Force the application to logout and login again if it has the intermittent ClientAuthError
        // in order to renew the refresh tokens
        const flashMessagesService = this.injector.get(FlashMessagesService) as FlashMessagesService;
        const spinnerService = this.injector.get(SpinnerService) as SpinnerService;
        const router = this.injector.get(Router);
        const location = this.injector.get(Location);
        const titleService = this.injector.get(Title);
        const ngZone = this.injector.get(NgZone);

        // Check if the current history state indicates this was a retry attempt
        let isRetryAttempt = false;
        if (history.state && history.state.isRetry) {
            isRetryAttempt = true;
        }

        if (error instanceof ClientAuthError) {
            flashMessagesService.clearAll();
            spinnerService.start();
            window.location.href = window.location.origin;
        } else {
            const returnUrl = location.path();
            const returnTitle = titleService.getTitle();

            // State for standard info (passed via state still reliable for URL/Title)
            const errorState = { returnUrl: returnUrl, returnTitle: returnTitle };
            // Query Params for the persistent flag
            const errorQueryParams = {};

            if (isRetryAttempt) {
                errorQueryParams['persistent'] = 'true'; // Add as query parameter
            }

            ngZone.run(() => {
                // Navigate with state for URL/Title and queryParams for persistent flag
                router.navigate(['/error'], {
                    state: errorState,
                    queryParams: errorQueryParams,
                    replaceUrl: isRetryAttempt
                });
            });

            spinnerService.reset();
        }

        // HACK: Explicitly tell NG2 to redraw (so we can actually see our message).
        // See: https://stackoverflow.com/questions/35105374/how-to-force-a-components-re-rendering-in-angular-2
        // TODO: FlashMessages should probably be Observable so we can avoid needing this hack.
        // const applicationRef = this.injector.get(ApplicationRef);
        // applicationRef.tick();
    }
}
